import { Form, FormGroupInputControl } from 'gwx-ui';
import {
	clearExceptions, connectedComponent, deleteInternalDomain, InternalDomains, loadFacility, retrieveInternalProviders, saveFacility, validateDomains
} from 'gwx-voyager-components';
import PropTypes from 'prop-types';
import { parse } from 'query-string';
import React, { Component } from 'react';

/**
 * Returns whether the provided email is semantically valid.
 *
 * @param {String} email
 */
function validateEmail(email) {
	const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(email);
}

/**
 * SendingFacilityForm component provides a form used to update
 * a Sending Facility resource.
 *
 * @class SendingFacilityForm
 * @extends {Component}
 */
class SendingFacilityForm extends Component {
	constructor(props) {
		super(props);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.deleteInternalDomain = this.deleteInternalDomain.bind(this);
		this.updateInternalDomains = this.updateInternalDomains.bind(this);
		this.closeInternalDomainModal = this.closeInternalDomainModal.bind(this);
		this.completeFormSubmit = this.completeFormSubmit.bind(this);
		this.loadConfirmation = this.loadConfirmation.bind(this);
		this.inputDisplayNames = {
			displayName: 'Display Name',
			inReplyTo: 'Reply-To'
		};
		this.disableOnEdit = ['displayName'];
		this.state = {
			isEmailInvalid: false,
			buttonsDisabled: false,
			sendingFacility: props.sendingFacility,
			internalDomainConfirmOpen: false
		};
	}

	componentDidMount() {
		this.props.clearExceptions();
		if (this.props.fetchSendingFacility) {
			this.props.fetchSendingFacility(this.props.sendingFacilityId);
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if ((Object.keys(this.props.exceptions).length === 0
			&& Object.keys(nextProps.exceptions).length !== 0)
			|| this.props.exceptions.timeStamp !== nextProps.exceptions.timeStamp) {
			this.setState({ buttonsDisabled: false });
		}

		if (nextProps.sendingFacilityId !== this.props.sendingFacilityId) {
			this.props.clearExceptions();
			this.props.fetchSendingFacility(nextProps.sendingFacilityId);
		}

		if (JSON.stringify(nextProps.sendingFacility)
			!== JSON.stringify(this.props.sendingFacility)) {
			this.setState({
				sendingFacility: nextProps.sendingFacility
			});
		}
	}

	componentWillUnmount() {
		this.props.clearExceptions();
	}

	handleInputChange(e) {
		const { target } = e;
		this.setState((prevState) => ({
			sendingFacility: { ...prevState.sendingFacility, [target.name]: target.value }
		}));
		if (this.props.exceptions) {
			this.props.clearExceptions();
		}
	}

	handleSubmit(e) {
		e.preventDefault();
		if (this.state.internalDomainConfirmOpen) {
			return;
		}

		if (this.state.internalDomains && this.state.internalDomains.filter((a) => a !== '').length > 0) {
			let invalidDomains = false;
			const invalidDomainMsgs = new Map();
			this.state.internalDomains.forEach((value) => {
				if (value && !validateEmail(`test@${value}`)) {
					invalidDomains = true;
					invalidDomainMsgs.set(value, `The domain ${value} is invalid.`);
				}
			});
			this.setState({ invalidDomainMsgs });
			if (!invalidDomains) {
				this.props.validateDomains(this.state.internalDomains).then((response) => this.loadConfirmation(response));
			}
		} else {
			this.completeFormSubmit();
		}
	}

	loadConfirmation(untrustedDomainsResult) {
		if (untrustedDomainsResult && Object.keys(untrustedDomainsResult.response.result).length === 0) {
			this.props.fetchInternalProviders(this.props.sendingFacilityId, this.state.internalDomains);
			this.setState({ internalDomainConfirmOpen: true });
		}
	}

	completeFormSubmit() {
		if ((this.state.sendingFacility.inReplyTo && !validateEmail(this.state.sendingFacility.inReplyTo.trim()))) {
			this.props.exceptions.fields = {
				inReplyTo: 'Invalid email address.'
			};
			this.setState({ isEmailInvalid: true });
		} else {
			const sendingFacility = {
				...this.state.sendingFacility,
				inReplyTo: this.state.sendingFacility && this.state.sendingFacility.inReplyTo ? this.state.sendingFacility.inReplyTo.trim() : '',
				newInternalDomains: this.state.internalDomains
			};
			const query = {
				...this.props.query,
				communityId: sendingFacility.communityId
			};
			this.setState({ buttonsDisabled: true }, () => this.props
				.save(sendingFacility, this.props.sendingFacilityId, query)
				.then((response) => this.props.discard(response)));
		}
	}

	deleteInternalDomain(id) {
		return this.props.deleteInternalDomain(id, { id: this.props.sendingFacilityId });	
	}

	updateInternalDomains(internalDomains) {
		const domains = internalDomains.map((domain) => (domain.domain));
		this.setState({ internalDomains: domains });
	}

	closeInternalDomainModal() {
		this.setState({ internalDomainConfirmOpen: false });
	}

	render() {
		const inputArr = Object.keys(this.inputDisplayNames).map((input) => {
			const inError = !!(this.props.exceptions.fields && this.props.exceptions.fields[input]);
			return (
				<FormGroupInputControl
					control={({
						id: input,
						name: input,
						onChange: this.handleInputChange,
						value: this.state.sendingFacility[input],
						disabled: this.disableOnEdit.indexOf(input) > -1,
						componentRef: (ref) => (this[input] = ref),
						inError
					})}
					helpText={inError ? this.props.exceptions.fields[input] : null}
					key={input}
					label={this.inputDisplayNames[input]}
				/>
			);
		});

		const formProps = {
			buttonsDisabled: this.state.buttonsDisabled,
			legend: 'Edit Sending Facility',
			onClickDiscard: this.props.discard,
			onSubmit: this.handleSubmit,
			style: 'sendingFacilityForm'
		};

		return (
			<Form {...formProps}>
				{inputArr}
				<InternalDomains
					updateInternalDomains={this.updateInternalDomains}
					closeInternalDomainModal={this.closeInternalDomainModal}
					deleteInternalDomain={(id) => this.deleteInternalDomain(id)}
					internalDomains={this.state.sendingFacility.sendingFacilityInternalDomains}
					completeFormSubmit={this.completeFormSubmit}
					internalDomainConfirmOpen={this.state.internalDomainConfirmOpen}
					isFetching={this.props.isFetching}
					internalProviders={this.props.internalProviders}
					untrustedDomains={this.props.untrustedDomains}
					invalidDomainMsgs={this.state.invalidDomainMsgs}
				/>
			</Form>
		);
	}
}
SendingFacilityForm.defaultProps = {
	sendingFacilityId: null,
	sendingFacility: {
		communityId: '',
		contactInfo: '',
		inReplyTo: '',
		contactName: '',
		displayName: '',
		hierarchicDesignator: '',
		timezoneRegion: 'America/Chicago'
	},
	isFetching: false,
	internalProviders: null,
	untrustedDomains: null
};
SendingFacilityForm.propTypes = {
	/**
	 * Function for clearing the exceptions from the
	 * store.
	 */
	clearExceptions: PropTypes.func.isRequired,
	/**
	 * Function for discarding the form.
	 */
	discard: PropTypes.func.isRequired,
	/**
	 * A set of exceptions that may be displayed
	 * in the form.
	 */
	exceptions: PropTypes.shape({
		fields: PropTypes.object,
		timeStamp: PropTypes.number
	}).isRequired,
	/**
	 * Function that is provided that will retrieve
	 * a Sending Facility by id.
	 */
	fetchSendingFacility: PropTypes.func.isRequired,
	/**
	 * Function for saving the Sending Facility resource.
	 */
	save: PropTypes.func.isRequired,
	/**
	 * The Sending Facility that is in context.
	 * This will only be populated when editing
	 * a Sending Facility.
	 */
	sendingFacility: PropTypes.shape({
		communityId: PropTypes.string,
		displayName: PropTypes.string,
		inReplyTo: PropTypes.string,
		hierarchicDesignator: PropTypes.string,
		contactName: PropTypes.string,
		contactInfo: PropTypes.string
	}),
	/**
	 * The Sending Facility ID.  This is used to retrieve
	 * the Sending Facility and indicates that this form
	 * should be used in edit mode.
	 */
	sendingFacilityId: PropTypes.string,
	/**
	 * Function for deleting a internal domains from the facility
	 */
	deleteInternalDomain: PropTypes.func.isRequired,
	fetchInternalDomainProviders: PropTypes.func.isRequired,
	validateDomains: PropTypes.func.isRequired,
	isFetching: PropTypes.bool,
	internalProviders: PropTypes.object,
	untrustedDomains: PropTypes.object
};

/**
 * Maps the current state of the Redux store to a set of properties
 * to be passed to the component.
 *
 * @param {object} state
 */
const mapStateToProps = (state, { match: { params }, location }) => {
	const {
		exceptions,
		entities: {
			sendingFacility, sendingFacilities, internalProviders, untrustedDomains
		}
	} = state;

	return {
		sendingFacility: sendingFacility[params.sfid],
		sendingFacilityId: params.sfid,
		internalProviders: internalProviders && internalProviders.results,
		untrustedDomains: untrustedDomains && untrustedDomains.results,
		isFetching: sendingFacility.isFetching || sendingFacilities.isFetching || internalProviders.isFetching || untrustedDomains.isFetching,
		exceptions: exceptions || {},
		query: { size: '25', number: '1', ...parse(location.search) }
	};
};

/**
 * Returns a set of functions that can utilize a dispatcher to dispatch actions.
 * These are the action creators for the SendingFacilityForm
 */
const mapDispatchToProps = (dispatch, props) => ({
	clearExceptions: () => { dispatch(clearExceptions); },
	discard: (response) => {
		if (!response || !response.error) {
			dispatch(clearExceptions);
			props.history.push(`/${props.match.params.id}/administration/${props.location.search}`);
		}
	},
	save: (facility, id, query) => dispatch(saveFacility(id, facility, query)),
	/**
	 * Fetches the Sending Facility if the id is provided, otherwise
	 * it dispatches resetSendingFacility and clears the previous
	 * Sending Facility that was stored.
	 */
	fetchSendingFacility: (id) => dispatch(loadFacility(id)),
	deleteInternalDomain: (id, sendingFacilityId) => dispatch(deleteInternalDomain(id, sendingFacilityId)),
	fetchInternalProviders: (sendingFacilityId, domains) => dispatch(retrieveInternalProviders(sendingFacilityId, domains)),
	validateDomains: (domains) => dispatch(validateDomains(domains))
});

const SendingFacilityFormContainer = connectedComponent(SendingFacilityForm, mapStateToProps, mapDispatchToProps);

export {
	SendingFacilityFormContainer as default,
	mapDispatchToProps,
	SendingFacilityForm
};
